import { MiniBasketDisplayItem } from '../components/miniBasket/miniBasketTypes';
import { 
  GroupedMainItem, 
  MiniBasketItem, 
  CTProduct,
  CTLineItem,
  CartCustomType
} from '../types/cartTypes';

/** Minimal type describing a product price discount object from Commercetools */
interface DiscountValue {
  type?: string;
  money?: Array<{ currencyCode: string; centAmount: number }>;
  [key: string]: unknown;
}

/** Minimal type describing a discount object returned from price.discounted */
interface DiscountObj {
  value?: DiscountValue;
  [key: string]: unknown;
}

/**
 * Normalizes a MiniBasketDisplayItem (or MiniBasketItem) into a GroupedMainItem shape.
 * This is used when bridging UI items back to service-layer calls like removeItem or updateQuantity.
 */
export function normalizeToGroupedMainItem(item: MiniBasketDisplayItem | MiniBasketItem | CTLineItem): GroupedMainItem {
    
    // Safely extract price as a number
    let price = 0;
    if (typeof item.price === 'number') {
        price = item.price * 100; // Convert back to cents if it was decimal
    }
    
    // totalPrice handling
    let totalPrice = 0;
    if (item.totalPrice?.centAmount) {
        totalPrice = item.totalPrice.centAmount;
    } else if (typeof item.price === 'number') {
        totalPrice = price * item.quantity;
    }

    // Custom fields bridging
    const custom: CartCustomType = item.custom || {
        type: { typeId: 'type', id: '' },
        fields: {}
    };

    return {
        id: item.id,
        productId: (item as unknown as { productId?: string }).productId ?? '',
        productKey: (item as unknown as { productKey?: string }).productKey ?? '',
        variantId: item.variant?.id || 1,
        quantity: item.quantity,
        name: typeof (item as unknown as { name: unknown }).name === 'string'
            ? (item as unknown as { name: string }).name
            : (Object.values((item as unknown as { name: Record<string, string> }).name || {})[0] || ''),
        price,
        totalPrice,
        custom,
        currency: (item as unknown as { currencyCode?: string }).currencyCode
            || (item as CTLineItem).totalPrice?.currencyCode
            || 'USD',
        isMainItem: true,
        isAddOn: false
    };
}

/**
 * Normalizes an array of items to GroupedMainItem array (add-ons).
 * Accepts the same union as normalizeToGroupedMainItem.
 */
export function normalizeToAddOns(
  items: Array<MiniBasketDisplayItem | MiniBasketItem | CTLineItem>
): GroupedMainItem[] {
    return items.map(item => {
        const normalized = normalizeToGroupedMainItem(item);
        return {
            ...normalized,
            isMainItem: false,
            isAddOn: true
        };
    });
}

/**
 * Safely casts a product response to CTProduct[] while handling structural variations.
 */
export function normalizeProducts(products: unknown[]): CTProduct[] {
    return products.map(p => {
        const prod = p as Record<string, unknown>;
        return {
            id: prod.id as string,
            key: prod.key as string,
            version: prod.version as number || 1,
            masterData: prod.masterData || {
                current: {
                    name: prod.name || {},
                    categories: prod.categories || [],
                    masterVariant: prod.masterVariant || { attributes: [], prices: [] },
                    slug: prod.slug || {}
                }
            },
            name: prod.name,
            categories: prod.categories,
            masterVariant: prod.masterVariant
        } as CTProduct;
    });
}

/**
 * Helper to safely extract a discount object from a price that might be a number or a PriceValue.
 * Returns a typed DiscountObj so callers can safely access .value.type and .value.money.
 */
export function getDiscountObj(
  price: number | { discounted?: { discount?: { obj?: unknown } } } | undefined
): DiscountObj | undefined {
    if (price && typeof price === 'object' && 'discounted' in price) {
        return price.discounted?.discount?.obj as DiscountObj | undefined;
    }
    return undefined;
}
